import MyLocationIcon from '@mui/icons-material/MyLocation';
import SearchIcon from '@mui/icons-material/Search';
import { Box, IconButton, useTheme } from '@mui/material';
import InputBase from '@mui/material/InputBase';
import { Map as OLMap } from 'ol';
import React from 'react';
import FontAwesome from 'react-fontawesome';

import { useVisitPostalCode } from '../lib/helpers';
import { tryParseInt } from '../lib/numbers';

import { MAX_POSTAL_DIGITS, MIN_POSTAL_DIGITS } from '././Form/inputTypes/PostalRangeInput';
import useCurrentLocation from './Map/hooks/useCurrentLocation';

const inputProps = {
  max: MAX_POSTAL_DIGITS,
  min: MIN_POSTAL_DIGITS,
  type: 'number',
};

interface SearchPostalFormProps {
  map?: OLMap;
  setSelectedPostalCode?: (digits: number) => void;
}

const SearchPostalForm: React.FC<SearchPostalFormProps> = ({ map, setSelectedPostalCode }) => {
  const theme = useTheme();
  const [postalCode, setPostalCode] = React.useState<number | undefined>(undefined);
  const { recentPostalCodes, visitPostalCode } = useVisitPostalCode();
  const [locationLoading, handleCurrentLocationClick] = useCurrentLocation(map);
  const ref = React.useRef<HTMLElement>();

  const handlePostalClick = setSelectedPostalCode || visitPostalCode;

  return (
    <React.Fragment>
      <Box
        component="form"
        sx={{
          alignItems: 'center',
          display: 'flex',
        }}
        onSubmit={(e) => {
          e.preventDefault();

          if (postalCode && postalCode > MIN_POSTAL_DIGITS && postalCode < MAX_POSTAL_DIGITS) {
            ref.current?.blur();
            handlePostalClick(postalCode);
          }
        }}
      >
        <InputBase
          inputProps={inputProps}
          inputRef={ref}
          name="postal-input"
          placeholder="1234"
          sx={{
            background: 'rgb(248, 248, 248)',
            flex: 1,
            padding: '2px 10px',
          }}
          value={postalCode ?? ''}
          onChange={(e) => {
            e.preventDefault();
            setPostalCode(tryParseInt(e.target.value));
          }}
        />
        <IconButton name="submit-search" size="large" type="submit">
          <SearchIcon />
        </IconButton>
        {map && (
          <IconButton size="large" onClick={handleCurrentLocationClick}>
            {locationLoading ? <FontAwesome spin name="spinner" /> : <MyLocationIcon />}
          </IconButton>
        )}
      </Box>
      <Box alignItems="center" display="flex" justifyContent="space-between">
        <p>
          {recentPostalCodes
            .map<JSX.Element>((digits) => (
              <Box
                component="button"
                key={digits}
                sx={{
                  color: theme.palette.secondary.main,
                  cursor: 'pointer',
                  fontWeight: 'bold',
                }}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  handlePostalClick(parseInt(digits));
                }}
              >
                {digits}
              </Box>
            ))
            .reduce<JSX.Element[]>(
              (result, item, index) => [
                ...result,
                <React.Fragment key={index}>{result.length > 0 ? ', ' : ''}</React.Fragment>,
                item,
              ],
              [],
            )}
        </p>
      </Box>
    </React.Fragment>
  );
};

export default SearchPostalForm;
